import { useMemo, useEffect } from 'react';
import { TradingText } from '@trading-ui/core';
import TradingSelect from '@trading-ui/select';
import { useTranslation } from 'react-i18next';

import useAppStore from '@/store/app.store';

import { AppUtils } from '@/utils/app.utils';
import { ROLES } from '@/constants/roles';

import type { User } from '@/interfaces/app.interfaces';

import useSettingsStore from '../../store/useSettingsStore';

import * as styles from './CompanyDetailsActionBar.css';

type Props = {
  user: User;
};

export default function CompanyDetailsActionBar({ user }: Props) {
  const masters = useAppStore((state) => state.masters);
  const selectedCompany = useSettingsStore((state) => state.selectedCompany);
  const setSelectedCompany = useSettingsStore((state) => state.setSelectedCompany);

  const { t } = useTranslation();

  const activeCompany = AppUtils.getActiveCompany(user);

  // Get company options based on role
  const companyOptions = useMemo(() => {
    if (user.role === ROLES.MOEVE_ADMIN) {
      return AppUtils.formatCompaniesToSelectOptions(masters.companies);
    }
    // For MANAGER, use their assigned companies
    return (user.companies ?? []).map((uc) => ({
      id: uc.companyId,
      label: uc.company?.name ?? '',
      value: uc.company?.name ?? '',
    }));
  }, [user.role, user.companies, masters.companies]);

  const hasOnlyOneCompany = companyOptions.length === 1;

  // Auto-select company if user has only one
  useEffect(() => {
    if (hasOnlyOneCompany && !selectedCompany) {
      setSelectedCompany(companyOptions[0]);
    }
  }, [hasOnlyOneCompany, selectedCompany, companyOptions, setSelectedCompany]);

  return (
    <div className={styles.root}>
      {user.role === ROLES.MOEVE_ADMIN || user.role === ROLES.MANAGER ? (
        <TradingSelect
          value={selectedCompany}
          options={companyOptions}
          onChange={(value) => setSelectedCompany(value)}
          label={t('settings.companyDetailsTab.company')}
          style={{ width: '250px' }}
          disabled={hasOnlyOneCompany}
        />
      ) : (
        <TradingText text={activeCompany?.company?.name ?? ''} variant="h6" />
      )}
    </div>
  );
}
